import React from 'react';

interface SelectProps {
  value?: string | number | any[];
  size?: 'small' | 'medium';
  variant?: 'outlined' | 'filled' | 'standard';
  disabled?: boolean;
  error?: boolean;
  displayEmpty?: boolean;
  label?: string;
  labelId?: string;
  multiple?: boolean;
  renderValue?: (selected: string | number | any[]) => React.ReactNode;
  onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  className?: string;
  children?: React.ReactNode;
  sx?: React.CSSProperties;
  id?: string;
}

const Select: React.FC<SelectProps> = ({
  value,
  onChange,
  size = 'medium',
  variant = 'outlined',
  disabled = false,
  error = false,
  displayEmpty = false,
  label,
  labelId,
  multiple = false,
  renderValue,
  className = '',
  children,
  sx,
  id,
}) => {
  const baseClasses = 'block w-full rounded border transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-0';
  
  const sizeClasses = {
    small: 'px-2 py-1 text-sm',
    medium: 'px-3 py-2 text-base',
  };

  const variantClasses = {
    outlined: error 
      ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
      : 'border-gray-300 dark:border-gray-600 focus:border-blue-500 focus:ring-blue-500',
    filled: error
      ? 'bg-red-50 dark:bg-red-900/20 border-red-500 focus:border-red-500 focus:ring-red-500'
      : 'bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600 focus:border-blue-500 focus:ring-blue-500',
    standard: error
      ? 'border-b-2 border-red-500 focus:border-red-500'
      : 'border-b-2 border-gray-300 dark:border-gray-600 focus:border-blue-500',
  };

  const classes = `${baseClasses} ${sizeClasses[size]} ${variantClasses[variant]} ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-800' : 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100'} ${className}`;

  const selectValue = multiple ? (Array.isArray(value) ? value : []) : value;

  if (multiple && renderValue && Array.isArray(selectValue)) {
    return (
      <div className="relative">
        {label && (
          <label htmlFor={labelId || id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {label}
          </label>
        )}
        <div className={classes} style={sx}>
          {renderValue(selectValue)}
        </div>
        <select
          id={labelId || id}
          value={Array.isArray(value) ? value.map(String) : []}
          onChange={onChange}
          disabled={disabled}
          multiple={true}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          style={{ zIndex: 1 }}
        >
          {children}
        </select>
      </div>
    );
  }

  if (multiple) {
    return (
      <div className="relative">
        {label && (
          <label htmlFor={labelId || id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {label}
          </label>
        )}
        <select
          id={labelId || id}
          value={Array.isArray(value) ? value.map(String) : []}
          onChange={onChange}
          disabled={disabled}
          multiple={true}
          className={classes}
          style={sx}
        >
          {children}
        </select>
      </div>
    );
  }

  return (
    <div className="relative">
      {label && (
        <label htmlFor={labelId || id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label}
        </label>
      )}
      <select
        id={labelId || id}
        value={multiple ? undefined : (selectValue as string | number)}
        onChange={onChange}
        disabled={disabled}
        multiple={multiple}
        className={classes}
        style={sx}
      >
        {displayEmpty && !multiple && <option value="">Select...</option>}
        {children}
      </select>
    </div>
  );
};

export default Select;

